import { useState, useCallback } from "react";

import {
  SignatureUpdateData,
  SignatureUpdateMethodResponse
} from "@/components/Shared/SignatureSelectorModal/interfaces";
import { FetchResponse } from "@/core/libs/api/utils/fetchData";
import { sleep } from "@/utils/Sleep";

interface UseUpdateSignatureMethodOptions {
  onUpdate: (
    signatureData: SignatureUpdateData
  ) => Promise<FetchResponse<SignatureUpdateMethodResponse>>;
  onUpdateSuccess?: () => void;
  refetchData?: (options?: { changeFetchingState?: boolean }) => Promise<void>;
}

export const useUpdateSignatureMethod = ({
  onUpdate,
  onUpdateSuccess,
  refetchData
}: UseUpdateSignatureMethodOptions) => {
  const REFETCH_DELAY = 3000;

  const [isLoading, setIsLoading] = useState(false);

  const handleUpdateSignatureMethod = useCallback(
    async (signatureData: SignatureUpdateData) => {
      setIsLoading(true);

      try {
        const response = await onUpdate(signatureData);

        if (response.error) return false;

        onUpdateSuccess?.();

        if (refetchData) {
          await sleep(REFETCH_DELAY);
          await refetchData({ changeFetchingState: false });
        }

        return true;
      } finally {
        setIsLoading(false);
      }
    },
    [onUpdate, onUpdateSuccess, refetchData]
  );

  const isButtonDisabled = isLoading;

  return {
    isLoading,
    isButtonDisabled,
    handleUpdateSignatureMethod
  };
};
